import React, { useState } from 'react';
import { BellRing, Clock, Phone, AlertTriangle, Users, Settings, Star } from 'lucide-react';
import { BathroomSafetyConfig } from '../BathroomSafety';

interface EmergencyResponseSettingsProps {
  config: BathroomSafetyConfig;
  setConfig: React.Dispatch<React.SetStateAction<BathroomSafetyConfig>>;
}

interface Contact {
  id: string;
  name: string;
  relation: string;
  phone: string;
  notifyFor: ('emergency' | 'slip' | 'anomaly' | 'extendedVisit')[];
  priority: number;
}

const mockContacts: Contact[] = [
  {
    id: 'contact-1',
    name: 'Sarah Martinez',
    relation: 'Daughter',
    phone: '(555) 123-4567',
    notifyFor: ['emergency', 'slip', 'anomaly', 'extendedVisit'],
    priority: 1
  },
  {
    id: 'contact-2',
    name: 'Robert Johnson',
    relation: 'Son',
    phone: '(555) 987-6543',
    notifyFor: ['emergency', 'slip'],
    priority: 2
  },
  {
    id: 'contact-3',
    name: 'Dr. Emily Chen',
    relation: 'Primary Care Physician',
    phone: '(555) 456-7890',
    notifyFor: ['emergency'],
    priority: 3
  }
];

const EmergencyResponseSettings: React.FC<EmergencyResponseSettingsProps> = ({ config, setConfig }) => {
  const [contacts, setContacts] = useState<Contact[]>(mockContacts);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState<boolean>(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>('');
  
  // Handler for toggle switches
  const handleToggle = (key: keyof BathroomSafetyConfig) => {
    if (typeof config[key] === 'boolean') {
      setConfig({
        ...config,
        [key]: !config[key]
      });
      
      // Show confirmation message
      const readableKey = key.replace(/([A-Z])/g, ' $1').toLowerCase();
      setConfirmationMessage(`${config[key] ? 'Disabled' : 'Enabled'} ${readableKey}`);
      setIsConfirmationVisible(true);
      setTimeout(() => setIsConfirmationVisible(false), 3000);
    }
  };
  
  // Handler for dropdown selects
  const handleSelectChange = (key: keyof BathroomSafetyConfig, value: string | number) => {
    setConfig({
      ...config,
      [key]: value
    });
    
    // Show confirmation message
    const readableKey = key.replace(/([A-Z])/g, ' $1').toLowerCase();
    setConfirmationMessage(`Updated ${readableKey} to ${value}`);
    setIsConfirmationVisible(true);
    setTimeout(() => setIsConfirmationVisible(false), 3000);
  };
  
  // Update contact notification settings
  const updateContactNotification = (contactId: string, notificationType: 'emergency' | 'slip' | 'anomaly' | 'extendedVisit') => {
    setContacts(currentContacts => 
      currentContacts.map(contact => {
        if (contact.id === contactId) {
          const updatedNotifyFor = contact.notifyFor.includes(notificationType)
            ? contact.notifyFor.filter(type => type !== notificationType)
            : [...contact.notifyFor, notificationType];
          
          return { ...contact, notifyFor: updatedNotifyFor };
        }
        return contact;
      })
    );
  };
  
  return (
    <div className="p-6">
      <h3 className="text-xl font-medium mb-6">Emergency Response & Alert Settings</h3>
      
      {/* Main Settings */}
      <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
        <h4 className="text-lg font-medium mb-4 flex items-center">
          <Settings className="mr-2 text-gray-600" />
          Alert Configuration
        </h4>
        
        <div className="space-y-6">
          {/* Emergency Response */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Emergency Response</p>
              <p className="text-sm text-gray-500">
                Automatically contact emergency services for severe incidents
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={config.emergencyResponseActive}
                onChange={() => handleToggle('emergencyResponseActive')}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {/* Caregiver Alert Threshold */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="font-medium text-gray-900">Caregiver Alert Threshold</p>
            </div>
            <select 
              className="w-full p-2 border border-gray-300 rounded-md"
              value={config.caregiverAlertThreshold}
              onChange={(e) => handleSelectChange('caregiverAlertThreshold', e.target.value as 'immediate' | 'delayed' | 'scheduledOnly')}
            >
              <option value="immediate">Immediate (Alert for all detected events)</option>
              <option value="delayed">Delayed (Wait for confirmation or timeout)</option>
              <option value="scheduledOnly">Scheduled Only (Daily reports, no immediate alerts)</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Determines when caregivers are notified of bathroom events
            </p>
          </div>
          
          {/* Slip Detection Sensitivity */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="font-medium text-gray-900">Slip Detection Sensitivity</p>
            </div>
            <select 
              className="w-full p-2 border border-gray-300 rounded-md"
              value={config.slipDetectionSensitivity}
              onChange={(e) => handleSelectChange('slipDetectionSensitivity', e.target.value as 'low' | 'medium' | 'high')}
            >
              <option value="low">Low (Fewer alerts, only severe incidents)</option>
              <option value="medium">Medium (Balanced detection)</option>
              <option value="high">High (Detect subtle slip events, more alerts)</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Higher sensitivity may generate more false positives
            </p>
          </div>
          
          {/* Water Alert */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Water Detection Alerts</p>
              <p className="text-sm text-gray-500">
                Alert when water is detected on bathroom floor
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={config.waterAlertEnabled}
                onChange={() => handleToggle('waterAlertEnabled')}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {/* Extended Visit Threshold */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="font-medium text-gray-900">Extended Visit Alert Threshold</p>
              <p className="text-sm font-medium text-gray-600">{config.extendedVisitThreshold} minutes</p>
            </div>
            <input 
              type="range" 
              min="5" 
              max="60" 
              step="5"
              value={config.extendedVisitThreshold}
              onChange={(e) => handleSelectChange('extendedVisitThreshold', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>5 min</span>
              <span>60 min</span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Alert if bathroom visit exceeds this duration
            </p>
          </div>
          
          {/* Nighttime Monitoring */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Nighttime Monitoring</p>
              <p className="text-sm text-gray-500">
                Enhanced monitoring between 10pm and 6am
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={config.nighttimeMonitoringEnabled}
                onChange={() => handleToggle('nighttimeMonitoringEnabled')}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {/* Auto Lighting */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Automatic Lighting</p>
              <p className="text-sm text-gray-500">
                Trigger smart lights when entering bathroom at night
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={config.autoLightingEnabled}
                onChange={() => handleToggle('autoLightingEnabled')}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>
      
      {/* Contact Settings */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h4 className="text-lg font-medium mb-4 flex items-center">
          <Users className="mr-2 text-gray-600" />
          Emergency Contacts & Notifications
        </h4>
        
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notify For</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contacts.map((contact) => (
              <tr key={contact.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center justify-center">
                    <div className="h-8 w-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-800">
                      {contact.priority}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                    <div className="text-sm text-gray-500">{contact.relation}</div>
                    <div className="text-sm text-gray-500">{contact.phone}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => updateContactNotification(contact.id, 'emergency')}
                      className={`px-2 py-1 text-xs rounded-full flex items-center ${
                        contact.notifyFor.includes('emergency') 
                          ? 'bg-red-100 text-red-800 border border-red-300' 
                          : 'bg-gray-100 text-gray-500 border border-gray-300'
                      }`}
                    >
                      <AlertTriangle size={12} className="mr-1" />
                      Emergency
                    </button>
                    <button
                      onClick={() => updateContactNotification(contact.id, 'slip')}
                      className={`px-2 py-1 text-xs rounded-full flex items-center ${
                        contact.notifyFor.includes('slip') 
                          ? 'bg-orange-100 text-orange-800 border border-orange-300' 
                          : 'bg-gray-100 text-gray-500 border border-gray-300'
                      }`}
                    >
                      <AlertTriangle size={12} className="mr-1" />
                      Slip Detected
                    </button>
                    <button
                      onClick={() => updateContactNotification(contact.id, 'anomaly')}
                      className={`px-2 py-1 text-xs rounded-full flex items-center ${
                        contact.notifyFor.includes('anomaly') 
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
                          : 'bg-gray-100 text-gray-500 border border-gray-300'
                      }`}
                    >
                      <BellRing size={12} className="mr-1" />
                      Anomalies
                    </button>
                    <button
                      onClick={() => updateContactNotification(contact.id, 'extendedVisit')}
                      className={`px-2 py-1 text-xs rounded-full flex items-center ${
                        contact.notifyFor.includes('extendedVisit') 
                          ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                          : 'bg-gray-100 text-gray-500 border border-gray-300'
                      }`}
                    >
                      <Clock size={12} className="mr-1" />
                      Extended Visit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <div className="mt-4">
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center"
          >
            <Phone className="w-4 h-4 mr-2" />
            Add New Contact
          </button>
        </div>
      </div>
      
      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center"
        >
          <Star className="w-4 h-4 mr-2" />
          Save Settings
        </button>
      </div>
      
      {/* Confirmation Message */}
      {isConfirmationVisible && (
        <div className="fixed bottom-6 right-6 bg-black bg-opacity-80 text-white px-4 py-2 rounded-md shadow-lg">
          {confirmationMessage}
        </div>
      )}
    </div>
  );
};

export default EmergencyResponseSettings; 